import React, { useRef } from "react";
import { Helmet } from "react-helmet-async";
import Footer from "../../components/Footer";
import { useArticlesContext } from "../../context/ArticlesContext";
import MobileMapOverlay from "../../components/MobileMapOverlay";
import SocialMedia from "../../components/SocialMedia";
import Marker from "../../components/Marker";
import Top from "../../components-top/Top";
import Left from "../../components-top/Left";
import Right from "../../components-top/Right";
import Bottom from "../../components-top/Bottom";

export default function BeginnersVancouverMushroomForaging() {
  // 1) Provide a unique reference ID (matching the slug in your ArticlesContext)
  const ref_id = "beginners-vancouver-mushroom-foraging";

  // 2) Access articles from your context (optional usage)
  const { articles } = useArticlesContext();
  const ref_data = articles.find((art) => art.slug === ref_id);

  // =========================================================
  // ========== SECTION TITLES IDS (Labels Map) ==============
  // =========================================================
  const labelsMap = {
    intro: "Introduction",
    "common-edible": "Common Edible Mushrooms",
    "where-to-forage": "Where to Forage",
    "foraging-safety": "Foraging Safety",
    "local-regulations": "Local Regulations",
    faq: "FAQ",
    conclusion: "Conclusion",
    references: "References",
  };

  // ===================================
  // 4) References Data
  // ===================================
  const referencesData = [
    {
      url: "https://vanmyco.org",
      label: "Vancouver Mycological Society",
    },
    {
      url: "https://www2.gov.bc.ca/gov/content/industry/natural-resource-use",
      label: "BC Government – Crown Land Usage",
    },
    {
      text: "MacKinnon, A. & Luther, K. (2021). 'Mushrooms of British Columbia.' Royal BC Museum.",
    },
  ];

  // ===================================
  // 5) Locations for the Leaflet map
  // ===================================
  // The "id" must match the sections or elements you refer to in the article text.
  const locations = [
    {
      id: "cat-lake",
      lat: 49.80,
      lng: -123.11,
      popupText: "Cat Lake Area (Squamish, BC)",
    },
    {
      id: "stave-west",
      lat: 49.37,
      lng: -122.45,
      popupText: "Stave West (Maple Ridge / Mission)",
    },
    {
      id: "ubc-campus",
      lat: 49.26,
      lng: -123.25,
      popupText: "UBC Campus Grounds",
    },
  ];

  // Keep references to textual sections for scroll linking
  const spotRefs = useRef({});
  const setSpotRef = (id, el) => {
    spotRefs.current[id] = el;
  };

  // Desktop map reference
  const mapRef = useRef(null);

  // MOBILE MAP
  const mobileMapRef = useRef(null);

  return (
    <>
      {/* ============ SEO & METADATA ============ */}
      <Helmet>
        <title>Beginner’s Vancouver Mushroom Foraging</title>
        <meta
          name="description"
          content="Learn how to safely and sustainably forage mushrooms in Vancouver. Explore legal locations, edible species, and key foraging tips."
        />
        <meta
          name="keywords"
          content="Vancouver, mushroom foraging, wild mushrooms, BC foraging"
        />
        <meta name="robots" content="index, follow" />
        <link
          rel="canonical"
          href="https://www.bcouver.com/articles/beginners-vancouver-mushroom-foraging"
        />

        {/* Open Graph / Facebook */}
        <meta
          property="og:title"
          content="Beginner’s Vancouver Mushroom Foraging"
        />
        <meta
          property="og:description"
          content="Learn how to safely and sustainably forage mushrooms in Vancouver. Explore legal locations, edible species, and key foraging tips."
        />
        <meta
          property="og:image"
          content="https://bcouver.com/assets/images/beginners-vancouver-mushroom-foraging.jpg"
        />
        <meta
          property="og:url"
          content="https://www.bcouver.com/articles/beginners-vancouver-mushroom-foraging"
        />
        <meta property="og:type" content="article" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Beginner’s Vancouver Mushroom Foraging"
        />
        <meta
          name="twitter:description"
          content="Learn how to safely and sustainably forage mushrooms in Vancouver. Explore legal locations, edible species, and key foraging tips."
        />
        <meta
          name="twitter:image"
          content="https://bcouver.com/assets/images/beginners-vancouver-mushroom-foraging.jpg"
        />
      </Helmet>

      {/* ============ TOP NAVBAR + HERO ============ */}
      <Top ref_id={ref_id} />

      <main>
        <div className="container article-main">
          {/* LEFT COLUMN (TableOfContents + Map) */}
          <Left
            labelsMap={labelsMap}
            locations={locations}
            mapRef={mapRef}
            spotRefs={spotRefs}
          />

          {/* ============ ARTICLE CONTENT ============ */}
          <article className="article-content">
            {/* Featured Image */}
            <img
              src={
                ref_data?.image ||
                "https://bcouver.com/assets/images/article-fallback.jpg"
              }
              alt={
                ref_data?.["image-desc"] ||
                "Beginner’s Vancouver Mushroom Foraging"
              }
              className="featured-image"
            />

            {/* Social Share */}
            <SocialMedia />

            {/* SECTION: Introduction */}
            <h2 id="intro">Introduction</h2>
            <p>
              Vancouver’s lush forests and damp climate create the perfect
              environment for a variety of wild mushrooms to flourish. From
              vibrant chanterelles to hearty oyster mushrooms, foraging can be
              a rewarding way to connect with nature and discover new flavors.
              In this guide, we’ll explore basic foraging tips, common edible
              species, safe and legal picking locations, and the regulations
              every beginner should know.
            </p>

            {/* SECTION: Common Edible Mushrooms */}
            <h2 id="common-edible">Common Edible Mushrooms</h2>
            <p>
              As a beginner, focus on a few easy-to-recognize mushrooms before
              branching out. Always confirm your finds using multiple sources
              (field guides, apps, local experts) to avoid dangerous mistakes.
              Here are some popular options:
            </p>
            <ul>
              <li>
                <strong>Pacific Golden Chanterelle:</strong> Bright golden cap
                with thick, forked ridges underneath. Emits a fruity, apricot-like
                aroma. Grows in conifer forests on the forest floor.
              </li>
              <li>
                <strong>Oyster Mushroom:</strong> Fan-shaped, grayish-white caps
                that grow on dead or dying wood. Often found in urban environments.
              </li>
              <li>
                <strong>Puffballs:</strong> Round, ball-like fungi with no visible
                stem or gills. Safe only if the interior is pure white when cut in
                half.
              </li>
              <li>
                <strong>Chicken of the Woods:</strong> Orange to yellow layered
                “shelves” on tree trunks. Tastes somewhat like chicken when cooked.
                Always test a small amount first, as some individuals have reactions.
              </li>
              <li>
                <strong>Shaggy Mane:</strong> Tall, cylindrical cap with shaggy
                white scales that turn black and inky as it ages. Eat them promptly
                after picking.
              </li>
            </ul>

            {/* SECTION: Where to Forage */}
            <h2 id="where-to-forage">Where to Forage</h2>
            <p>
              Foraging is prohibited in municipal, regional, and provincial parks.
              Instead, seek out Crown land sites that permit personal-use mushroom
              harvesting. Be sure to confirm boundaries, because collecting
              mushrooms in protected areas can result in fines.
            </p>
            <p
              ref={(el) => setSpotRef("cat-lake", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Cat Lake (Squamish, BC)</strong>{" "}
              <Marker
                coordY={49.80}
                coordX={-123.11}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              : Located north of Squamish on Crown land. Known for second-growth
              conifer forests that produce chanterelles in fall.
            </p>
            <p
              ref={(el) => setSpotRef("stave-west", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Stave West (Maple Ridge / Mission)</strong>{" "}
              <Marker
                coordY={49.37}
                coordX={-122.45}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              : Features mixed woodlands near logging roads. Ideal for puffballs
              and the occasional oyster mushroom.
            </p>
            <p
              ref={(el) => setSpotRef("ubc-campus", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>UBC Campus Grounds</strong>{" "}
              <Marker
                coordY={49.26}
                coordX={-123.25}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              : Urban foraging can be done on some public property outside of
              designated parks. Look for oysters and shaggy manes in lawn and
              treed areas, but avoid cultivated gardens or research plots.
            </p>

            {/* SECTION: Foraging Safety */}
            <h2 id="foraging-safety">Foraging Safety</h2>
            <p>
              <strong>Identify with Certainty:</strong> Always cross-check your
              finds with reputable field guides or local mushroom clubs. A single
              misidentified mushroom can lead to serious health risks.
            </p>
            <p>
              <strong>Cook Thoroughly & Test:</strong> Many wild mushrooms are
              difficult to digest raw. Cook them well and start with a small
              portion if you’re eating a species for the first time.
            </p>
            <p>
              <strong>Leave No Trace:</strong> Take only what you need, and don’t
              disturb the forest floor unnecessarily. This helps maintain the
              ecosystem for future mushroom flushes and other wildlife.
            </p>

            {/* SECTION: Local Regulations */}
            <h2 id="local-regulations">Local Regulations</h2>
            <p>
              In British Columbia, picking mushrooms is generally permitted on
              Crown land if it’s for personal use. However, it’s illegal in
              all national parks, provincial parks, regional parks, and city
              parks (e.g., Stanley Park, Pacific Spirit, etc.). If you want to
              harvest commercially, you will need a permit. Always respect
              private property boundaries and any First Nations land where
              separate permissions may be required.
            </p>

            {/* SECTION: FAQ */}
            <h2 id="faq">FAQ</h2>
            <div className="faq-section">
              <h3>1. What equipment do I need for mushroom foraging?</h3>
              <p>
                A sturdy basket or breathable bag, a small knife for trimming
                stems, a brush to remove debris, and a reliable field guide. You
                might also want a GPS or map, especially when venturing outside
                urban areas.
              </p>

              <h3>2. How can I learn mushroom identification more thoroughly?</h3>
              <p>
                Join a local mycology group like the Vancouver Mycological
                Society, attend guided foraging tours, and consult multiple
                field guides. Hands-on experience with experts is invaluable.
              </p>
            </div>

            {/* SECTION: Conclusion */}
            <h2 id="conclusion">Conclusion</h2>
            <p>
              Exploring Vancouver’s forests and green spaces in search of wild
              mushrooms can be an exciting adventure—just remember to do it
              safely and ethically. Respect local regulations, seek permission
              where necessary, and expand your identification skills gradually.
              With the right approach, mushroom foraging offers both a culinary
              thrill and a deeper connection to the natural world.
            </p>

            {/* BOTTOM COMPONENT: references, author, related articles, etc. */}
            <Bottom referencesData={referencesData} ref_data={ref_data} />
          </article>

          {/* RIGHT SIDE COLUMN */}
          <Right currentArticle={ref_data} />
        </div>
      </main>

      {/* MOBILE MAP OVERLAY */}
      <MobileMapOverlay
        ref={mobileMapRef}
        markers={locations}
        spotRefs={spotRefs}
      />

      <Footer />
    </>
  );
}